import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import milestoneTrackingService from '../../../services/milestoneTrackingService';

const ClientDrillDownModal = ({ client, onClose, onRefresh }) => {
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEmailLog();
  }, [client?.id]);

  const loadEmailLog = async () => {
    const { data } = await milestoneTrackingService?.getMilestoneEmailLog(client?.id);
    setEmailLog(data || []);
  };

  const getMilestoneIcon = (type) => {
    const icons = {
      organization_profile: 'Building',
      employee_import: 'Users',
      compliance_setup: 'Shield',
      team_access: 'UserCheck'
    };
    return icons?.[type] || 'Circle';
  };

  const getMilestoneLabel = (type) => {
    const labels = {
      organization_profile: 'Organization Profile',
      employee_import: 'Employee Import',
      compliance_setup: 'Compliance Setup',
      team_access: 'Team Access'
    };
    return labels?.[type] || type;
  };

  const getStatusColor = (status) => {
    if (status === 'completed') return 'text-success';
    if (status === 'in_progress') return 'text-warning';
    return 'text-muted-foreground';
  };

  const calculateTimeToComplete = (milestone) => {
    if (!milestone?.started_at || !milestone?.completed_at) return 'N/A';
    const start = new Date(milestone?.started_at);
    const end = new Date(milestone?.completed_at);
    const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  const handleSendReminder = async (milestoneType) => {
    setLoading(true);
    try {
      await milestoneTrackingService?.sendMilestoneEmail(
        milestoneType,
        client?.companyName,
        client?.contactEmail,
        'Please complete this milestone to continue your onboarding'
      );
      alert('Reminder sent successfully');
      await loadEmailLog();
    } catch (error) {
      alert('Failed to send reminder: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">{client?.companyName}</h2>
            <p className="text-sm text-muted-foreground">{client?.contactEmail}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Overall Progress */}
          <div className="bg-muted/30 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Overall Progress</h3>
              <span className="text-3xl font-bold text-primary">{client?.overallProgress}%</span>
            </div>
            <div className="w-full h-4 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${client?.overallProgress}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Time in Current Stage</p>
                <p className="text-lg font-semibold text-foreground">{client?.timeInStage} days</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Risk Level</p>
                <p className={`text-lg font-semibold ${
                  client?.riskLevel === 'high' ? 'text-error' :
                  client?.riskLevel === 'medium' ? 'text-warning' : 'text-success'
                }`}>
                  {client?.riskLevel?.toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Next Milestone</p>
                <p className="text-lg font-semibold text-foreground">
                  {client?.nextMilestone ? getMilestoneLabel(client?.nextMilestone?.milestone_type) : 'Complete'}
                </p>
              </div>
            </div>
          </div>

          {/* Milestone Breakdown */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Milestone Breakdown</h3>
            <div className="space-y-4">
              {client?.milestones?.map((milestone, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded ${getStatusColor(milestone?.milestone_status)} bg-current/10`}>
                        <Icon name={getMilestoneIcon(milestone?.milestone_type)} size={20} color="currentColor" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {getMilestoneLabel(milestone?.milestone_type)}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          Status: {milestone?.milestone_status?.replace('_', ' ')?.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-foreground">{milestone?.completion_percentage || 0}%</p>
                      <p className="text-xs text-muted-foreground">
                        Time: {calculateTimeToComplete(milestone)}
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${milestone?.completion_percentage || 0}%` }}
                    ></div>
                  </div>
                  {milestone?.milestone_status !== 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendReminder(milestone?.milestone_type)}
                      disabled={loading}
                      className="w-full"
                    >
                      <Icon name="Send" size={14} className="mr-2" />
                      Send Reminder
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Email Log */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Communication History</h3>
            <div className="space-y-2">
              {emailLog?.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No emails sent yet</p>
              ) : (
                emailLog?.map((log, index) => (
                  <div key={index} className="bg-muted/30 rounded p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon
                        name={log?.email_status === 'sent' ? 'CheckCircle' : 'AlertCircle'}
                        size={16}
                        className={log?.email_status === 'sent' ? 'text-success' : 'text-error'}
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {getMilestoneLabel(log?.milestone_type)} - {log?.email_type}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(log?.created_at)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium ${
                      log?.email_status === 'sent' ? 'text-success' : 'text-error'
                    }`}>
                      {log?.email_status?.toUpperCase()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Close
          </Button>
          <Button
            onClick={() => {
              onRefresh();
              onClose();
            }}
            variant="primary"
            className="flex-1"
          >
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientDrillDownModal;